import React, { useState, useEffect, useCallback } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Spinner,
    Button,
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from "reactstrap";
import { useSearchParams } from "react-router-dom";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import CategoryInfiniteScroll from "../../../components/CategoryInfiniteScroll/CategoryInfiniteScroll";
import { getAssetsByCategoryCode } from "../../../services/ValitanApiService";
import { exportToCSV, exportToExcel } from "../../../utils/exportUtils";

// Import all asset category tabs
import BuildingsTab from "./Tabs/BuildingsTab";
import EquipmentTab from "./Tabs/EquipmentTab";
import FurnitureTab from "./Tabs/FurnitureTab";
import ICTTab from "./Tabs/ICTTab";
import LandTab from "./Tabs/LandTab";
import MachineryTab from "./Tabs/MachineryTab";
import VehiclesTab from "./Tabs/VehiclesTab";
// import SiteWorksTab from "./Tabs/SiteWorksTab"; // Muted - hidden from UI

const BRAND_BLUE = "#0147C8";
const DEFAULT_PAGE_SIZE = 10;

const CATEGORY_CODE_MAP = {
    buildings: "BLDG",
    equipment: "EQP",
    furniture: "FURN",
    ict: "ICT",
    land: "LAND",
    machinery: "MACH",
    vehicles: "VEH",
    // siteworks: "SITE", // Muted - hidden from UI
};

const TAB_KEYS = Object.keys(CATEGORY_CODE_MAP);

const makeInitialPaginationByTab = () =>
    TAB_KEYS.reduce((acc, key) => {
        acc[key] = { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE };
        return acc;
    }, {});

const AssetsByCategory = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const categoryParam = searchParams.get("category") || "buildings";
    
    // Validate category param and default to "buildings" if invalid
    const initialTab = TAB_KEYS.includes(categoryParam) ? categoryParam : "buildings";
    
    const [activeTab, setActiveTab] = useState(initialTab); // Default to buildings tab or from URL
    const [assetsByCategory, setAssetsByCategory] = useState({}); // Store ALL loaded assets per category (accumulated)
    const [loadingByCategory, setLoadingByCategory] = useState({}); // Loading state per category
    const [loadingMoreByCategory, setLoadingMoreByCategory] = useState({}); // Loading more state
    const [paginationByTab, setPaginationByTab] = useState(makeInitialPaginationByTab);
    const [exportDropdownOpen, setExportDropdownOpen] = useState(false);

    // Get export headers based on category
    const getExportHeaders = (category) => {
        const commonHeaders = [
            { key: "serialNumber", label: "#" },
            { key: "name", label: "Name" },
            { key: "barcode", label: "Barcode" },
            { key: "observed_condition_name", label: "Condition" },
            { key: "institution_name", label: "Institution" },
        ];

        const categorySpecificHeaders = {
            buildings: [
                { key: "name", label: "Building Name" },
                { key: "building_number", label: "Building No." },
                { key: "plot_size", label: "Plot Size (m²)" },
                { key: "building_use", label: "Use" },
                { key: "has_water", label: "Water" },
                { key: "has_electricity", label: "Electricity" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            furniture: [
                { key: "serialNumber", label: "#" },
                { key: "barcode", label: "Barcode" },
                { key: "serial_number", label: "Serial Number" },
                { key: "name", label: "Asset Name" },
                { key: "quantity", label: "Qty" },
                { key: "institution_name", label: "Institution" },
                { key: "building_name", label: "Building" },
                { key: "floor", label: "Floor" },
                { key: "room_name_or_number", label: "Room" },
                { key: "acquisition_date", label: "Acquisition Date" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            equipment: [
                { key: "name", label: "Name" },
                { key: "description", label: "Description" },
                { key: "location", label: "Location" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            ict: [
                { key: "barcode", label: "Barcode" },
                { key: "name", label: "Name" },
                { key: "location", label: "Location" },
                { key: "acquisition_date", label: "Acquisition Date" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            land: [
                { key: "name", label: "Name" },
                { key: "plot_number", label: "Plot No." },
                { key: "plot_size", label: "Size (m²)" },
                { key: "certificate_of_title", label: "Title" },
                { key: "location", label: "Location" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            machinery: [
                { key: "name", label: "Name" },
                { key: "description", label: "Description" },
                { key: "location", label: "Location" },
                { key: "observed_condition_name", label: "Condition" },
            ],
            vehicles: [
                { key: "registration_number", label: "Registration" },
                { key: "chassis_number", label: "Chassis No." },
                { key: "make", label: "Make" },
                { key: "model", label: "Model" },
                { key: "fuel_type", label: "Fuel Type" },
                { key: "engine_capacity", label: "Engine Capacity (cc)" },
                { key: "year_of_manufacture", label: "Year" },
                { key: "institution_name", label: "Institution" },
                { key: "observed_condition_name", label: "Condition" },
            ],
        };

        return categorySpecificHeaders[category] || commonHeaders;
    };

    // Export to CSV
    const handleExportCSV = () => {
        const assets = assetsByCategory[activeTab] || [];
        if (assets.length === 0) {
            alert("No data to export");
            return;
        }

        const headers = getExportHeaders(activeTab);
        const filename = `${activeTab}_assets_${new Date().toISOString().split("T")[0]}.csv`;
        exportToCSV(assets, headers, filename);
    };

    // Export to Excel
    const handleExportExcel = () => {
        const assets = assetsByCategory[activeTab] || [];
        if (assets.length === 0) {
            alert("No data to export");
            return;
        }

        const headers = getExportHeaders(activeTab);
        const filename = `${activeTab}_assets_${new Date().toISOString().split("T")[0]}.xlsx`;
        exportToExcel(assets, headers, filename);
    };

    // Update active tab when URL param changes
    useEffect(() => {
        if (categoryParam && TAB_KEYS.includes(categoryParam)) {
            setActiveTab(categoryParam);
        }
    }, [categoryParam]);

    // Fetch assets by category code with pagination (server-side) - supports append mode for infinite scroll
    const fetchAssetsByCategory = useCallback(async (tabKey, pageNumber = 0, pageSize = DEFAULT_PAGE_SIZE, append = false) => {
        if (!CATEGORY_CODE_MAP[tabKey]) return;

        const categoryCode = CATEGORY_CODE_MAP[tabKey];
        
        // Use different loading states for initial load vs loading more
        if (append) {
            setLoadingMoreByCategory((prev) => ({ ...prev, [tabKey]: true }));
        } else {
            setLoadingByCategory((prev) => ({ ...prev, [tabKey]: true }));
        }
        
        try {
            const res = await getAssetsByCategoryCode(categoryCode, pageNumber, pageSize);
            const responseData = res.data?.data || res.data || [];
            const pagination = res.data?.pagination || {};
            
            // Accumulate assets (append) or replace based on append flag
            setAssetsByCategory((prev) => {
                if (append) {
                    // Append new assets to existing ones
                    const existing = prev[tabKey] || [];
                    return { ...prev, [tabKey]: [...existing, ...responseData] };
                } else {
                    // Replace with new assets (initial load or tab change)
                    return { ...prev, [tabKey]: responseData };
                }
            });
            
            // Update pagination info from server
            setPaginationByTab((prev) => ({
                ...prev,
                [tabKey]: {
                    pageNumber: pagination.page || pageNumber,
                    pageSize: pagination.size || pageSize,
                    totalElements: pagination.totalElements || 0,
                    totalPages: pagination.totalPages || 1,
                    hasMore: (pagination.page || pageNumber) < (pagination.totalPages || 1) - 1,
                },
            }));
        } catch (err) {
            console.error(`Failed to load ${categoryCode} assets:`, err);
            if (!append) {
                setAssetsByCategory((prev) => ({ ...prev, [tabKey]: [] }));
            }
            setPaginationByTab((prev) => ({
                ...prev,
                [tabKey]: {
                    pageNumber: prev[tabKey]?.pageNumber || 0,
                    pageSize: DEFAULT_PAGE_SIZE,
                    totalElements: prev[tabKey]?.totalElements || 0,
                    totalPages: prev[tabKey]?.totalPages || 1,
                    hasMore: false,
                },
            }));
        } finally {
            if (append) {
                setLoadingMoreByCategory((prev) => ({ ...prev, [tabKey]: false }));
            } else {
                setLoadingByCategory((prev) => ({ ...prev, [tabKey]: false }));
            }
        }
    }, []);

    // Fetch initial assets when tab changes or on mount
    useEffect(() => {
        // Reset and load first page when tab changes
        setAssetsByCategory((prev) => ({ ...prev, [activeTab]: [] }));
        setPaginationByTab((prev) => ({
            ...prev,
            [activeTab]: { pageNumber: 0, pageSize: DEFAULT_PAGE_SIZE, hasMore: true },
        }));
        fetchAssetsByCategory(activeTab, 0, DEFAULT_PAGE_SIZE, false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Load more function
    const loadMore = useCallback(() => {
        const pagination = paginationByTab[activeTab];
        if (!pagination || !pagination.hasMore || loadingMoreByCategory[activeTab]) return;
        
        const nextPage = pagination.pageNumber + 1;
        fetchAssetsByCategory(activeTab, nextPage, pagination.pageSize, true);
    }, [activeTab, paginationByTab, loadingMoreByCategory, fetchAssetsByCategory]);


    const renderCategoryTabContent = (tabKey, TabComponent) => {
        const currentAssets = assetsByCategory[tabKey] || [];
        const paginationInfo = paginationByTab?.[tabKey] || {
            pageNumber: 0,
            pageSize: DEFAULT_PAGE_SIZE,
            totalElements: 0,
            totalPages: 1,
            hasMore: false,
        };
        const isLoadingMore = loadingMoreByCategory[tabKey];
        const hasMore = paginationInfo.hasMore && !loadingByCategory[tabKey];

        // Add serial numbers to assets
        const assetsWithSerial = currentAssets.map((asset, index) => ({
            ...asset,
            serialNumber: index + 1, // 1-based indexing
        }));

        return (
            <TabPane tabId={tabKey}>
                {loadingByCategory[tabKey] && currentAssets.length === 0 ? (
                    <div className="text-center py-5">
                        <Spinner
                            style={{
                                width: "3.5rem",
                                height: "3.5rem",
                                color: BRAND_BLUE,
                            }}
                        />
                        <p className="mt-3 text-muted">Loading {tabKey} assets...</p>
                    </div>
                ) : (
                    <>
                        {currentAssets.length > 0 ? (
                            <>
                                <TabComponent assets={assetsWithSerial} />
                                
                                <CategoryInfiniteScroll
                                    currentCount={currentAssets.length}
                                    totalCount={paginationInfo.totalElements}
                                    hasMore={hasMore}
                                    isLoading={loadingByCategory[tabKey]}
                                    isLoadingMore={isLoadingMore}
                                    onLoadMore={loadMore}
                                    categoryName={tabKey}
                                />
                            </>
                        ) : (
                            !loadingByCategory[tabKey] && (
                                <p className="text-center text-muted py-4">No {tabKey} assets found.</p>
                            )
                        )}
                    </>
                )}
            </TabPane>
        );
    };

    const tabStyle = (isActive) => ({
        padding: "10px 16px",
        margin: "0 6px",
        borderRadius: "50px",
        fontWeight: "600",
        fontSize: "13px",
        color: isActive ? "white" : "#333",
        backgroundColor: isActive ? BRAND_BLUE : "transparent",
        border: "none",
        cursor: "pointer",
        transition: "all 0.25s ease",
        whiteSpace: "nowrap",
    });

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs 
                        title="Assets" 
                        breadcrumbItem={`Assets By Category - ${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}`} 
                    />
                    <Row>
                        <Col lg="12">
                            <Card>
                                <CardHeader>
                                    <div className="d-flex justify-content-end align-items-center">
                                        {assetsByCategory[activeTab]?.length > 0 && (
                                            <Dropdown
                                                isOpen={exportDropdownOpen}
                                                toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                                            >
                                                <DropdownToggle
                                                    caret
                                                    style={{
                                                        backgroundColor: BRAND_BLUE,
                                                        borderColor: BRAND_BLUE,
                                                        color: "white",
                                                    }}
                                                >
                                                    <i className="ri-download-line me-2"></i>
                                                    Export
                                                </DropdownToggle>
                                                <DropdownMenu>
                                                    <DropdownItem onClick={handleExportCSV}>
                                                        <i className="ri-file-text-line me-2"></i>
                                                        Export as CSV
                                                    </DropdownItem>
                                                    <DropdownItem onClick={handleExportExcel}>
                                                        <i className="ri-file-excel-line me-2"></i>
                                                        Export as Excel
                                                    </DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        )}
                                    </div>
                                </CardHeader>
                                <CardBody>

                                    <TabContent activeTab={activeTab}>
                                        {renderCategoryTabContent("buildings", BuildingsTab)}
                                        {renderCategoryTabContent("equipment", EquipmentTab)}
                                        {renderCategoryTabContent("furniture", FurnitureTab)}
                                        {renderCategoryTabContent("ict", ICTTab)}
                                        {renderCategoryTabContent("land", LandTab)}
                                        {renderCategoryTabContent("machinery", MachineryTab)}
                                        {renderCategoryTabContent("vehicles", VehiclesTab)}
                                        {/* SiteWorks tab muted - hidden from UI */}
                                    </TabContent>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default AssetsByCategory;
